import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const axios = require('axios').default;

export default function useTags({
  team, taggableType, taggableId, csrfToken,
}) {
  const [taggedItems, setTaggedItems] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [tagSearchValue, setTagSearchValue] = useState('');

  const teamId = team && team.id;

  const reloadTags = async () => {
    const [itemsResponse, tagsResponse] = await Promise.all([
      axios.get(`/teams/${teamId}/tagged_items`, {
        params: { taggable_type: taggableType, taggable_id: taggableId },
      }),
      axios.get(`/teams/${teamId}/crm/tags`, { params: { scope: taggableType } }),
    ]);

    setTaggedItems(itemsResponse.data || []);
    setAvailableTags(tagsResponse.data || []);
  };

  useEffect(() => {
    if (!teamId || !taggableId || !csrfToken) return;

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    reloadTags().catch(() => toast.error('Failed to load tags.'));
  }, [teamId, taggableType, taggableId, csrfToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.cdp-tag-popup-container')) setShowTagPopup(false);
    };

    if (showTagPopup) document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagPopup]);

  const onToggleTag = async (tag, isActive, taggedItem) => {
    if (!teamId || !taggableId || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      if (isActive && taggedItem) {
        await axios.delete(`/teams/${teamId}/tagged_items/${taggedItem.id}`);
      } else {
        await axios.post(`/teams/${teamId}/tagged_items`, {
          tagged_item: { taggable_id: taggableId, taggable_type: taggableType, tag_id: tag.id },
        });
      }

      await reloadTags();
    } catch (err) {
      toast.error(isActive ? 'Failed to remove tag.' : 'Failed to add tag.');
    }
  };

  const onCreateAndAddTag = async (tagName) => {
    if (!teamId || !taggableId || !csrfToken || !tagName.trim()) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      await axios.post(`/teams/${teamId}/tagged_items`, {
        tagged_item: {
          taggable_id: taggableId,
          taggable_type: taggableType,
          tag_attributes: { name: tagName },
        },
      });

      await reloadTags();
      setTagSearchValue('');
    } catch (err) {
      toast.error('Failed to create tag.');
    }
  };

  return {
    taggedItems,
    availableTags,
    showTagPopup,
    setShowTagPopup,
    tagSearchValue,
    setTagSearchValue,
    onToggleTag,
    onCreateAndAddTag,
  };
}
